<template>
  <div v-if="teaserImage">
    <CmsImage :blok="cmsDataContent as unknown as SbImage" is-teaser />
  </div>
  <PageContent>
    <div class="flex gap-5">
      <Sidebar />
      <div class="grow overflow-hidden p-0.5">
        <PLPHeader
          v-if="selectedCategory"
          :heading="heading ?? ''"
          :results-count="resultsCount || null"
          :fetching="fetching"
          :products="products"
          :products-fetching="productsFetching"
          :pre-listing-content="preListingContent"
          :post-listing-content="postListingContent"
          :is-first-page="isFirstPage"
          :filters="filters"
          :plp-sorting-values="plpSortingValues"
          :selected-category="selectedCategory"
          :categories="categories"
          @reset-price-filter="resetPriceFilter"
          @apply-attribute-filter="applyAttributeFilter"
          @reset-filter="resetFilter"
        />

        <!-- <DesktopFilters
          v-if="filters && !isMobileOrTablet && showNewDesktopPlpQuickFilters"
          :active-filters="activeFilters"
          :filters="filters"
          :unfiltered-filters="unfilteredFilters"
          :values="plpSortingValues"
          :product-count="productCountFilter"
          :plp-sorting-values="plpSortingValues"
          @filter:state-changed="updateFilterCount($event)"
          @filter:apply="applyFilters($event)"
          @filter:reset="resetFilterUrl"
        /> -->

        <DesktopFiltersNew
          v-if="filters && !isMobileOrTablet && showNewDesktopPlpQuickFilters"
          :applied-filter="appliedFilter"
          :filters="filters"
          :applied-boolean-values="appliedBooleanValues"
          :applied-attribute-values="appliedAttributeValues"
          :plp-sorting-values="plpSortingValues"
          :size-category-sort-order="sizeCategorySortOrder"
          @apply-boolean-filter="applyBooleanFilter"
          @apply-attribute-filter="applyAttributeFilter"
          @apply-price-filter="applyPriceFilter"
          @reset-filter="resetFilter"
          @reset-price-filter="resetPriceFilter"
        />

        <div
          v-if="!isMobileOrTablet && showNewDesktopPlpQuickFilters"
          class="mb-4 flex max-w-full flex-col"
        >
          <ActiveFilters
            v-if="activeFilters && unfilteredFilters"
            :filters="unfilteredFilters"
            :active-filters="activeFilters"
            @reset-price-filter="resetPriceFilter"
            @apply-attribute-filter="applyAttributeFilter"
            @reset-filter="resetFilter"
          />
        </div>

        <ProductList
          :aria-label="heading || selectedCategory?.name"
          :loading="productsFetching"
          :per-page="productsPerPage"
          :products="products"
          :priority-product-count="isMobile ? 2 : 4"
          :selected-category="selectedCategory"
          class="grid w-auto gap-little lg:gap-normal"
          :class="
            !resultsCount && !productsFetching
              ? 'grid-cols-1'
              : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
          "
          @click:product="onProductClick"
          @intersect:products="onProductsIntersect"
        >
          <template
            v-if="listingContent && isFirstPage && !productsFetching"
            #listing
          >
            <component
              :is="listing.component"
              v-for="listing in listingContent"
              :key="listing._uid"
              :blok="listing"
              :category="heading"
            />
          </template>
        </ProductList>

        <Pagination
          v-if="pagination"
          :current-page="pagination.page"
          :first-page="pagination.first"
          :last-page="pagination.last"
        />
      </div>
    </div>

    <RecommendationsConfigSection :bloks="plpRecommendations" class="my-10" />

    <div v-if="postListingContent && isFirstPage && !productsFetching">
      <component :is="isMobile ? 'ReadMore' : 'div'">
        <component
          :is="postContent.component"
          v-for="postContent in postListingContent"
          :key="postContent._uid"
          :blok="postContent"
        />
      </component>
    </div>

    <FilterSlideIn
      v-if="filters && filtersOpen"
      :category="plpPath"
      :applied-filter="appliedFilter"
      :filters="filters"
      :applied-boolean-values="appliedBooleanValues"
      :applied-attribute-values="appliedAttributeValues"
      :values="plpSortingValues"
      :size-category-sort-order="sizeCategorySortOrder"
      @reset-filters="resetFilters"
      @apply-boolean-filter="applyBooleanFilter"
      @apply-attribute-filter="applyAttributeFilter"
      @apply-price-filter="applyPriceFilter"
      @reset-filter="resetFilter"
      @reset-price-filter="resetPriceFilter"
      @apply-filters="applyFilters"
    />
  </PageContent>
</template>

<script setup lang="ts">
import { onMounted, watch, computed, nextTick } from 'vue'
import type { Product } from '@scayle/storefront-nuxt'

import Sidebar from './components/Sidebar.vue'

import { fetchOnNavigation } from '#layers/base/utils/loading'
import useSlideIn from '#layers/base/composables/useSlideIn'
import { useDeviceType } from '~/composables/useDeviceType'
import usePlpTracking from '#layers/base/composables/plp/usePlpTracking'
import usePlpQueryFilterState from '#layers/base/composables/plp/usePlpQueryFilterState'
import useNewPlp from '#layers/base/composables/plp/useNewPlp'
import usePlpQueryRefresh from '#layers/base/composables/plp/usePlpQueryRefresh'
import useStoryMetaData from '~/composables/useStoryMetaData'
import useCms from '~/composables/cms/useCms'
import RecommendationsConfigSection from '#layers/base/components/recommendations/RecommendationsConfigSection.vue'
import { RECO_CONFIG_SLUGS } from '#layers/base/constants/recommendations'

import { useRoute } from '#app/composables/router'
import { useCurrentShop } from '#storefront/composables'
import type {
  SbImage,
  SbContentFragmentsPage,
} from '~/cms/storyblok/types/storyblok'
import useExperiments from '~/composables/useExperiments'
import { useAppliedFilters } from '#layers/base/composables/useAppliedFilters'
import DesktopFiltersNew from '~/components/filters/DesktopFiltersNew.vue'
import usePlpFallbackWatcher from '#layers/base/composables/plp/usePlpFallbackWatcher'
import usePlpNavigationReconcile from '#layers/base/composables/plp/usePlpNavigationReconcile'
import usePlpSEO from '#layers/base/composables/plp/usePlpSEO'
import usePdpBreadcrumbContext from '#layers/base/composables/usePdpBreadcrumbContext'
import type { ListedProduct } from '~/types/tracking'
import { getFilterNames, getFilterOptions } from '~/utils/tracking'
import { runWhenIdle } from '#layers/base/utils/idle'

const { isOpen: filtersOpen } = useSlideIn('FilterSlideIn')

const { appliedFilter, appliedBooleanValues, appliedAttributeValues } =
  useAppliedFilters()
const { activeFilters } = usePlpQueryFilterState()
const { trackSelectItem, trackViewItemList, fireTrackingServerQueue } =
  usePlpTracking()
const { setNavigationContext } = usePdpBreadcrumbContext()

const route = useRoute()
const currentShop = useCurrentShop()
const app = useNuxtApp()

const { isMobile } = useDeviceType()

const { showNewDesktopPlpQuickFilters } = useExperiments()
const { isMobileOrTablet } = useDeviceType()

const plpPath = computed(() => {
  if (Array.isArray(route.params.slug)) {
    return `/${route.params.slug.join('/')}`
  }
  return `/${route.params.slug}`
})

const plp = useNewPlp()

const {
  categories: rawCategories,
  selectedCategory,
  products,
  productsFetching,
  resultsCount,
  productsPerPage,

  filters,
  selectedSort,
  unfilteredFilters,
  pagination,
  pageCount,
  isFirstPage,
  plpSortingValues,

  filterProducts,

  fetch,
  fetching,

  cmsDataContent,
  teaserImage,
  listingContent,
  preListingContent,
  postListingContent,
  heading,

  applyBooleanFilter,
  applyAttributeFilter,
  applyPriceFilter,
  applyFilters,
  resetFilter,
  resetFilters,
  resetPriceFilter,
} = plp

const sizeCategorySortOrder = computed<string | null>(() => {
  const raw = selectedCategory.value?.shopLevelCustomData?.sizeCategorySortOrder
  return typeof raw === 'string' && raw.trim().length > 0 ? raw : null
})

const { fetch: fetchSEO, storyMetaData } = usePlpSEO(plp)

const categories = computed(() => {
  if (!rawCategories.value) {
    return []
  }

  if (Array.isArray(rawCategories.value)) {
    return rawCategories.value
  }

  return [rawCategories.value]
})

usePlpFallbackWatcher(plp)

// ECOM-2692: post-hydration self-heal for the browser-back race. Registered
// synchronously here; reconcile() is also called from onMounted below.
const { reconcile } = usePlpNavigationReconcile(plp)

// ECOM-2696: optional Storyblok-configured recommendation modules rendered
// between the pagination and the post-listing content. Generic
// `ContentFragmentsPage` story at content-fragments/plpPage (mirrors
// pages/success.vue), fetched once per locale; a missing/erroring story renders
// nothing (never fatal — R7). Keyed by locale so a locale switch in the same
// session refetches instead of serving the previous language's config (matches
// the locale-scoped keys in useShippingCostHint / useProductSizeGuide).
const { data: plpRecoConfig, fetchBySlug: fetchPlpRecoConfig } =
  useCms<SbContentFragmentsPage>(`plpRecoConfig-${currentShop.value.locale}`)

const loadPlpRecoConfig = async () => {
  if (plpRecoConfig.value) {
    return
  }
  try {
    await fetchPlpRecoConfig(RECO_CONFIG_SLUGS.plp)
  } catch {
    // Optional config — swallow so a CMS error never breaks the PLP (R7).
  }
}

// The PLP has a single render slot, so it renders the concatenation of every
// fragment's content in document order (id-agnostic) — a CM can group the
// modules under one or several ContentFragment bloks.
const plpRecommendations = computed(
  () =>
    plpRecoConfig.value?.content?.fragments?.flatMap(
      (fragment) => fragment.content ?? [],
    ) ?? [],
)

usePlpQueryRefresh(filterProducts)
const fetchPageData = async () => {
  await Promise.all([fetch(), fetchSEO(), loadPlpRecoConfig()])
}

watch(
  () => route.path,
  async () => {
    await nextTick()
    await fetchPageData()
  },
  { flush: 'post' },
)

onMounted(async () => {
  runWhenIdle(fireTrackingServerQueue)

  // Single reconciliation entry point (shared with the app:suspense:resolve
  // trigger inside usePlpNavigationReconcile). Covers both the ordinary
  // client-execution first product fetch and the browser-back hydration-race
  // heal, choosing fetch depth by what's actually missing. ECOM-2692.
  await reconcile()
})

await fetchOnNavigation(fetchPageData)

app.runWithContext(() => useStoryMetaData({ ...storyMetaData.value, fetching }))

const onProductClick = (product: Product, index: number) => {
  trackSelectItem({
    product,
    index,
    listType: 'category_page',
    listName: selectedCategory.value?.name ?? '',
  })
  // ECOM-2383: remember the PLP category path the product was opened from so the
  // PDP can render the matching breadcrumb (client-only, lost on hard reload).
  setNavigationContext(selectedCategory.value?.rootlineIds ?? [], product.id)
}

const onProductsIntersect = (items: ListedProduct[]) => {
  trackViewItemList({
    type: 'category_page',
    name: selectedCategory.value?.name ?? '',
    items,
    filters: getFilterNames(
      filters.value,
      appliedFilter.value,
      unfilteredFilters.value,
    ),
    filterOptions: getFilterOptions(
      filters.value,
      appliedFilter.value,
      unfilteredFilters.value,
    ),
    sorting: selectedSort.value?.name,
    totalItemQuantity: resultsCount.value ?? 0,
    totalPages: pageCount.value ?? 1,
    viewItemQuantity: products.value.length,
    currentPage: pagination.value?.page ?? 1,
  })
}
</script>
